import React, { useState, useEffect, useCallback } from 'react';
import { academyAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './TrainingOverview.css';

// Shape returned by /api/Academy/admin/all-progress
interface ApiUserProgress {
  id: number;
  username: string;
  fullName: string;
  role: string;
  email: string;
  trainingRequired: boolean;
  trackName: string;
  modulesCompleted: number;
  allPassed: boolean;
  certificateIssued: boolean;
  certRef?: string;
  issuedAt?: string;
  modules: { moduleIndex: number; score: number; passed: boolean; completedAt: string }[];
}

const fmt = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

const TrainingOverview: React.FC = () => {
  const [progress, setProgress]   = useState<ApiUserProgress[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [resetting, setResetting] = useState<number | null>(null);
  const [updatingRequirement, setUpdatingRequirement] = useState<number | null>(null);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';

  const fetchProgress = useCallback(() => {
    setError(null);
    setLoading(true);
    academyAPI
      .getAllProgress()
      .then((res) => {
        setProgress(res.data ?? []);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load training progress. Please try again.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const handleReset = async (userId: number) => {
    if (!window.confirm("Reset this user's Academy progress? This cannot be undone.")) return;
    setResetting(userId);
    try {
      await academyAPI.resetUser(userId);
      fetchProgress();
    } catch {
      alert('Failed to reset progress. Please try again.');
    } finally {
      setResetting(null);
    }
  };

  const handleTrainingRequirement = async (row: ApiUserProgress) => {
    const nextValue = !row.trainingRequired;
    const action = nextValue ? 'require' : 'exempt';
    if (!window.confirm(`${nextValue ? 'Require' : 'Mark not required'} Academy training for ${row.fullName || row.username}?`)) return;
    setUpdatingRequirement(row.id);
    try {
      await academyAPI.setTrainingRequirement(row.id, nextValue);
      fetchProgress();
    } catch (err: any) {
      alert(err.response?.data?.message || `Failed to ${action} training.`);
    } finally {
      setUpdatingRequirement(null);
    }
  };

  return (
    <div className="to-wrap">
      <div className="to-header-row">
        <div>
          <h3 className="to-heading">Training Overview</h3>
          <p className="to-sub">Academy progress across all registered users. {progress.length > 0 && `(${progress.length} users)`}</p>
        </div>
        <button className="to-refresh-btn" onClick={fetchProgress} disabled={loading}>
          {loading ? 'Loading…' : '↺ Refresh'}
        </button>
      </div>

      {error && <p className="to-error">{error}</p>}

      {!loading && !error && progress.length === 0 && (
        <p className="to-empty">No training records found.</p>
      )}

      {!loading && progress.length > 0 && (
        <div className="to-table-wrap">
          <table className="to-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Full Name</th>
                <th>Role</th>
                <th>Track</th>
                <th>Training</th>
                <th>Modules Passed</th>
                <th>Status</th>
                <th>Certificate</th>
                <th>Completed Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {progress.map((row) => {
                const passedCount = row.modules?.filter(m => m.passed).length ?? 0;
                return (
                  <tr key={row.id}>
                    <td className="to-username">{row.username}</td>
                    <td>{row.fullName || '—'}</td>
                    <td>
                      <span className="to-role-badge">{row.role}</span>
                    </td>
                    <td>{row.trackName || '—'}</td>
                    <td className="to-center">
                      <span className={`to-status ${row.trainingRequired ? 'to-status--progress' : 'to-status--complete'}`}>
                        {row.trainingRequired ? 'Required' : 'Not Required'}
                      </span>
                    </td>
                    <td className="to-center">
                      <span className={`to-modules ${row.allPassed ? 'to-modules--done' : ''}`}>
                        {passedCount}/5
                      </span>
                    </td>
                    <td>
                      {row.allPassed ? (
                        <span className="to-status to-status--complete">✓ Complete</span>
                      ) : passedCount > 0 ? (
                        <span className="to-status to-status--progress">In Progress</span>
                      ) : (
                        <span className="to-status to-status--notstarted">Not Started</span>
                      )}
                    </td>
                    <td className="to-center">
                      {row.certificateIssued ? (
                        <span style={{ color: '#276749', fontWeight: 700, fontSize: 13 }}>✓ Issued</span>
                      ) : (
                        <span style={{ color: '#a0aec0', fontSize: 13 }}>—</span>
                      )}
                    </td>
                    <td className="to-nowrap">{fmt(row.issuedAt)}</td>
                    <td>
                      {isSuperAdmin && (
                        <button
                          className="to-reset-btn"
                          onClick={() => handleTrainingRequirement(row)}
                          disabled={updatingRequirement === row.id}
                          title="Toggle whether Academy training is required for this user"
                          style={{ marginRight: 6 }}
                        >
                          {updatingRequirement === row.id
                            ? 'Saving…'
                            : row.trainingRequired ? 'Mark Not Required' : 'Require Training'}
                        </button>
                      )}
                      <button
                        className="to-reset-btn"
                        onClick={() => handleReset(row.id)}
                        disabled={resetting === row.id}
                        title="Reset this user's Academy progress"
                      >
                        {resetting === row.id ? 'Resetting…' : 'Reset'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {loading && <p className="to-loading">Loading training records…</p>}
    </div>
  );
};

export default TrainingOverview;
